import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { PageSEO } from '../components/SEO';

const faqCategories = [
  {
    title: 'Distribution Process',
    questions: [
      {
        question: 'How long until my film is live on platforms?',
        answer: 'From acceptance to going live, expect around 90 days. This includes our quality checks, poster and trailer production (if needed), platform encoding, metadata preparation, and delivery to all platforms. Some platforms may go live slightly earlier or later depending on their review processes.'
      },
      {
        question: 'What formats do you need?',
        answer: 'We require a ProRes 422 HQ or higher master file, ideally at 1920x1080 or 4K resolution. We also need stereo and/or 5.1 audio stems, closed caption files (SRT or VTT format), and your film\'s poster artwork if you have it. Don\'t worry if you\'re missing some elements—we can help create posters and trailers as part of our service.'
      },
      {
        question: 'Do you handle subtitles and closed captions?',
        answer: 'Yes. We process and format closed captions and subtitles for all platforms. If you have existing subtitle files, we\'ll format them to each platform\'s specifications. If you need subtitles created, we can arrange this as an additional service.'
      },
      {
        question: 'Which platforms will my film be on?',
        answer: 'We guarantee distribution to Amazon Prime Video, Apple TV, Tubi, Fandango, Plex, and JustWatch. We also pitch your film to Netflix, HBO Max, Shudder, and other premium platforms—though placement on these is not guaranteed and depends on their interest.'
      },
      {
        question: 'Can I choose which platforms my film appears on?',
        answer: 'Yes. If there are specific platforms you want to exclude (perhaps you already have a deal there), just let us know. However, we recommend maximising your reach by being on all available platforms.'
      },
    ]
  },
  {
    title: 'Submissions & Selection',
    questions: [
      {
        question: 'What types of films do you accept?',
        answer: 'We accept completed feature films and documentaries across all genres. We do not accept scripts, treatments, shorts, or projects still in production. Your film must be fully finished with all rights cleared before submission.'
      },
      {
        question: 'How selective are you?',
        answer: 'We\'re quite selective. We curate a quality catalogue rather than accepting everything. We look at the overall package—story, performances, production value, and whether there\'s a clear target audience. Not every film is right for us, and that\'s okay. We\'d rather be honest upfront than waste your time.'
      },
      {
        question: 'How long does the review process take?',
        answer: 'We aim to respond to all submissions within 2-3 weeks. If we\'re interested, we\'ll schedule a call to discuss next steps. If your film isn\'t right for us, we\'ll let you know—we don\'t leave filmmakers hanging.'
      },
      {
        question: 'Do I need festival experience or awards?',
        answer: 'Festival laurels and awards are helpful but not required. What matters most is the quality of the film itself and whether it has a clear audience. Some of our best-performing titles had minimal festival runs.'
      },
      {
        question: 'Can I submit a film that\'s already on some platforms?',
        answer: 'Potentially, yes. If your film is already on certain platforms but you want to expand its reach, we can discuss which territories or platforms are still available. Just be upfront about your existing distribution arrangements.'
      },
    ]
  },
  {
    title: 'Revenue & Payments',
    questions: [
      {
        question: 'How much do I keep?',
        answer: 'You keep 70% of all VOD revenue. We take a 30% commission which covers everything—poster design, trailer editing, encoding, delivery, and ongoing management. There are no hidden fees, setup costs, or per-platform charges.'
      },
      {
        question: 'When and how do I get paid?',
        answer: 'We provide monthly reports and payments. Revenue is typically reported by platforms 60-90 days after viewing, so there\'s a natural delay. Once we receive payment from platforms, we process your share within 30 days via bank transfer.'
      },
      {
        question: 'What kind of reports will I receive?',
        answer: 'You\'ll receive detailed monthly reports showing views and revenue broken down by platform and territory. No black boxes—you\'ll see exactly where your audience is watching and how much each platform is generating.'
      },
      {
        question: 'Are there any upfront costs?',
        answer: 'No. We don\'t charge any upfront fees, delivery fees, or platform fees. Our 30% commission only applies to revenue earned. If your film doesn\'t earn, you don\'t pay. It\'s that simple.'
      },
      {
        question: 'What about international revenue?',
        answer: 'Our distribution covers global territories. You\'ll receive revenue from viewers worldwide, with full transparency on which countries are generating views and income.'
      },
    ]
  },
  {
    title: 'Sales Agency Services',
    questions: [
      {
        question: 'What\'s the difference between distribution and sales?',
        answer: 'VOD distribution is getting your film on streaming platforms like Amazon and Tubi. Sales representation is pitching your film to traditional buyers—theatrical distributors, broadcast networks, and major streaming services like Netflix. We offer both services.'
      },
      {
        question: 'How do you pitch to Netflix, HBO, etc.?',
        answer: 'We have relationships with acquisitions executives at major platforms and studios. We pitch suitable films at markets (Cannes, Berlin, AFM) and through direct submissions. Acceptance isn\'t guaranteed—these platforms are highly selective—but we ensure your film gets seen by the right people.'
      },
      {
        question: 'What are your sales commission rates?',
        answer: 'Sales commission varies depending on the deal type and territory. Typical rates range from 15-25% depending on the scope of representation. We\'ll discuss specific terms if sales representation is right for your project.'
      },
      {
        question: 'Do you attend film markets?',
        answer: 'Yes. We maintain an active presence at Cannes Film Market, European Film Market (Berlin), American Film Market, TIFF Industry, and other major markets where buyers congregate.'
      },
    ]
  },
  {
    title: 'Marketing & Assets',
    questions: [
      {
        question: 'What if I don\'t have a poster?',
        answer: 'No problem. Our 20% commission includes professional poster design. We\'ll create key art that captures your film\'s essence and meets all platform specifications. You\'ll have approval on the final design.'
      },
      {
        question: 'What if I don\'t have a trailer?',
        answer: 'We\'ve got you covered. Our in-house editors can cut a compelling trailer from your film. Like posters, this is included in our commission—no extra charge. You\'ll approve the final edit before it goes live.'
      },
      {
        question: 'Can I use my own marketing materials?',
        answer: 'Absolutely. If you already have a poster and trailer you love, we\'ll use them. We\'ll just need to ensure they meet technical specifications for each platform. If any reformatting is needed, we handle that.'
      },
      {
        question: 'Do you offer additional marketing services?',
        answer: 'Yes. Beyond what\'s included in distribution, our marketing agency offers paid advertising campaigns, social media management, website development, and PR services. These are quoted separately based on your needs and budget.'
      },
    ]
  },
  {
    title: 'Legal & Rights',
    questions: [
      {
        question: 'What rights do you need?',
        answer: 'We require non-exclusive VOD distribution rights for the territories we\'re distributing to. You retain ownership of your film and can pursue other distribution opportunities simultaneously, as long as there\'s no conflict.'
      },
      {
        question: 'How long is the contract?',
        answer: 'Standard contracts are typically 3-5 years, which allows enough time for your film to find its audience and generate meaningful revenue. Terms can be discussed based on your specific situation.'
      },
      {
        question: 'Can I leave if I get a better offer?',
        answer: 'Our contracts include provisions for theatrical or major platform buyouts. If Netflix wants to acquire your film exclusively, we\'ll work with you to make that happen—we want what\'s best for your film\'s success.'
      },
      {
        question: 'Do I need errors & omissions insurance?',
        answer: 'E&O insurance is required by most platforms. If you don\'t have it, we can point you to providers who work with independent filmmakers at reasonable rates.'
      },
    ]
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-white/10">
      <button
        onClick={onClick}
        className="w-full py-6 flex items-center justify-between text-left group"
        data-testid={`faq-question-${question.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-white font-medium pr-8 group-hover:text-orange-500 transition-colors">
          {question}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-orange-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="text-neutral-400 pb-6 leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function FAQ() {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Generate FAQ Schema as string
  const faqSchemaString = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap(category => 
      category.questions.map(q => ({
        "@type": "Question",
        "name": q.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q.answer
        }
      }))
    )
  });

  return (
    <div data-testid="faq-page" className="min-h-screen pt-20 bg-neutral-950">
      <PageSEO page="faq" />
      <Helmet>
        <script type="application/ld+json">{faqSchemaString}</script>
      </Helmet>
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1920&q=80"
            alt="Cinema reel - frequently asked questions about UK film distribution"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-orange-500 font-sans text-sm tracking-[0.3em] uppercase mb-4">
              FREQUENTLY ASKED QUESTIONS
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight uppercase leading-none text-white mb-6">
              FAQ
            </h1>
            <p className="text-neutral-300 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Everything you need to know about working with LBR Distribution. Can't find your answer? Get in touch.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="mb-16"
            >
              <h2 className="font-display text-2xl md:text-3xl tracking-wider text-white uppercase mb-8 flex items-center gap-4">
                <span className="w-10 h-10 bg-orange-600 flex items-center justify-center text-sm">
                  {categoryIndex + 1}
                </span>
                {category.title}
              </h2>
              <div className="bg-neutral-900/30 border border-white/10 p-6 md:p-8">
                {category.questions.map((item, questionIndex) => (
                  <FAQItem
                    key={questionIndex}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openItems[`${categoryIndex}-${questionIndex}`]}
                    onClick={() => toggleItem(categoryIndex, questionIndex)}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-16 md:py-24 bg-neutral-900/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-6">
              STILL HAVE QUESTIONS?
            </h2>
            <p className="text-neutral-400 text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our team is happy to help. Send us a message and we'll get back to you within 48 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="faq-contact-btn"
                className="inline-flex items-center justify-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
              >
                CONTACT US
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/submit"
                data-testid="faq-submit-btn"
                className="inline-flex items-center justify-center gap-3 border border-white/20 hover:border-white text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all"
              >
                SUBMIT YOUR FILM
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
